import React from 'react';

/**
 * App-level error boundary. If any descendant component throws during render,
 * we swallow the crash and show a minimal branded fallback instead of leaving
 * the user with a blank #0a0a0a screen (the pre-mount body color on `/`).
 *
 * We also expose the error to the console (and window.__bl_last_error) so the
 * user can share it back to us if the fallback ever shows up in prod.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: (error && error.message) || 'Unknown error' };
  }

  componentDidCatch(error, info) {
    // Surface everywhere we reasonably can so we never lose the trace.
    // eslint-disable-next-line no-console
    console.error('Brick Lane render crash:', error, info);
    try {
      window.__bl_last_error = {
        message: (error && error.message) || String(error),
        stack: (error && error.stack) || '',
        info: info && info.componentStack ? info.componentStack : '',
        ts: new Date().toISOString(),
      };
    } catch (e) { /* ignore */ }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="app-error-fallback"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0a0a0a',
          color: '#fff',
          fontFamily: 'Georgia, serif',
          textAlign: 'center',
          padding: '2rem',
        }}
      >
        <div style={{ maxWidth: 520 }}>
          <div
            style={{
              fontSize: 'clamp(2.5rem, 6vw, 4rem)',
              letterSpacing: '0.02em',
              marginBottom: '0.75rem',
            }}
          >
            Brick Lane
          </div>
          <div style={{ opacity: 0.7, fontSize: '1.05rem', marginBottom: '1.75rem' }}>
            Los Angeles Soul. Indian Roots.
          </div>
          <p style={{ opacity: 0.7, fontSize: '0.95rem', lineHeight: 1.6, marginBottom: '1.5rem' }}>
            We hit a snag loading the page. Please refresh — if it persists, use the links below.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="/"
              onClick={(e) => { e.preventDefault(); window.location.reload(); }}
              style={{ color: '#C9A86C', textDecoration: 'underline' }}
            >
              Refresh
            </a>
            <a
              href="https://resy.com/cities/los-angeles-ca/venues/bricklane"
              target="_blank"
              rel="noreferrer"
              style={{ color: '#C9A86C', textDecoration: 'underline' }}
            >
              Reserve on Resy
            </a>
            <a
              href="tel:+12133950110"
              style={{ color: '#C9A86C', textDecoration: 'underline' }}
            >
              Call (213) 395-0110
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
